import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { GenericMessages } from 'src/intl/GenericMessages';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import './ConnectControllerModal.scss';
import { ConnectControllerDetails } from './ConnectControllerDetails';
import {
  selectBleConnectableDevices,
  selectBleConnectedDevice,
  selectBleConnectionState,
  selectBleIsConnectControllerModalOpen,
  selectBleIsLocked,
} from 'src/redux/ble/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { IonIcon, IonRow } from '@ionic/react';
import { DqText } from '../dq-text';
import { gameControllerOutline } from 'ionicons/icons';
import { BleService } from 'src/ble/BleService';
import { bleActions } from 'src/redux/ble/actions';
import { BleControllerDevice } from 'src/redux/ble/types';
import { BleConnectionState } from 'src/redux/ble/constants';
import { useHasBluetoothLE } from 'src/utils/helpers/platformHelpers';

import { useToast } from '../reusable-components/useToast';
import { HomeMessages } from 'src/intl/HomeMessages';

const _ConnectControllerModal: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [present] = useToast();

  const scanRef = React.useRef<{ abort: AbortController; promise: Promise<void> } | undefined>(undefined);
  const [hasFirstRequestOccurred, setHasFirstRequestOccurred] = useState<boolean>(false);

  const connectableDevices = useSelector(selectBleConnectableDevices);
  const bleState = useSelector(selectBleConnectionState);
  const isLoading = useSelector(selectBleIsLocked);
  const isConnectControllerModalOpen = useSelector(selectBleIsConnectControllerModalOpen);
  const connectedDevice = useSelector(selectBleConnectedDevice);
  const lastConnectionStateRef = React.useRef(bleState);

  const noDevicesDetectedAndNotLoading = !isLoading && connectableDevices.length === 0;
  const shouldShowScanDirections =
    noDevicesDetectedAndNotLoading &&
    (bleState !== BleConnectionState.CONNECTED ||
      (hasFirstRequestOccurred && bleState === BleConnectionState.CONNECTED));

  useEffect(() => {
    if (!isConnectControllerModalOpen) {
      setHasFirstRequestOccurred(false);
    }
  }, [isConnectControllerModalOpen]);

  useEffect(() => {
    if (
      (lastConnectionStateRef.current === BleConnectionState.DISCONNECTED || BleConnectionState.ATTEMPTING_CONNECT) &&
      bleState === BleConnectionState.CONNECTED
    ) {
      if (connectedDevice?.physicalSerial) {
        connectedDevice?.isControllerInOrg
          ? present(
              {
                header: formatMessage(HomeMessages.homeControllerConnectionSuccessMessage),
              },
              'success',
            )
          : present(
              {
                header: formatMessage(HomeMessages.homeControllerConnectionSuccessOtherOrgMessage),
              },
              'warning',
            );
      }
      dispatch(bleActions.closeConnectControllerModal());
    }
  }, [bleState, connectedDevice?.isControllerInOrg, connectedDevice?.physicalSerial, dispatch, formatMessage, present]);

  /**
   * Connect to the selected controller device.
   */
  const connectToDevice = useCallback(
    async (device: BleControllerDevice) => {
      try {
        scanRef.current?.abort.abort();
        if (scanRef.current) await scanRef.current.promise;
        if (connectedDevice) await BleService.disconnect(connectedDevice.deviceId);
        await BleService.connect(device);
        await BleService.controller?.listBundles();
        await BleService.controller?.listLogFiles();
      } catch (e) {
        console.error('Failed to connect to device', e);
        present(
          {
            header: formatMessage(ControllerMessages.connectControllerActionFailed),
          },
          'danger',
        );
      }
    },
    [connectedDevice, formatMessage, present],
  );

  const primaryButtonConfig = useMemo(
    () => ({
      label:
        !hasFirstRequestOccurred || isLoading
          ? formatMessage(ControllerMessages.scan)
          : formatMessage(ControllerMessages.scanAgain),
      onClick: async () => {
        // Abort any ongoing scanning process if it exists.
        scanRef.current?.abort.abort();
        // Wait for the current scanning promise to complete, if it exists.
        if (scanRef.current) await scanRef.current.promise;

        const abort = new AbortController();
        const promise = BleService.scan(abort.signal);
        scanRef.current = { abort, promise };

        setHasFirstRequestOccurred(true);
      },
      disabled: isLoading,
    }),
    [hasFirstRequestOccurred, isLoading, formatMessage],
  );

  const modalTitle = useMemo(() => {
    if (bleState === BleConnectionState.CONNECTED) {
      return formatMessage(ControllerMessages.changeControllerTitle);
    }
    return formatMessage(ControllerMessages.connectControllerTitle);
  }, [bleState, formatMessage]);

  const cancelScan = async () => {
    scanRef.current?.abort.abort();
    dispatch(bleActions.closeConnectControllerModal());
  };

  return (
    <AppModal
      isOpen={isConnectControllerModalOpen}
      onClose={() => cancelScan()}
      title={modalTitle}
      primaryButton={primaryButtonConfig}
      secondaryButton={{
        label: formatMessage(GenericMessages.cancelButtonLabel),
        onClick: () => cancelScan(),
      }}
    >
      {shouldShowScanDirections ? (
        <div className="scan-directions-container">
          <IonIcon className="controller-icon" icon={gameControllerOutline} />
          <IonRow className="ion-margin-top scan-directions-row">
            {hasFirstRequestOccurred && (
              <DqText variant="body-strong">{formatMessage(ControllerMessages.controllerNotFound)}</DqText>
            )}
          </IonRow>
          <IonRow className="scan-directions-row">
            <DqText variant="body">{formatMessage(ControllerMessages.controllerDirections)}</DqText>
          </IonRow>
        </div>
      ) : (
        <ConnectControllerDetails
          onCardClick={connectToDevice}
          isLoading={isLoading}
          connectableDevices={connectableDevices}
          connectedDevice={connectedDevice}
          isControllerConnecting={bleState === BleConnectionState.ATTEMPTING_CONNECT}
        />
      )}
    </AppModal>
  );
};

/** Wraps the ConnectControllerModal component and does bluetooth check up front, to avoid unnecessary networks calls associated with mounting the component. */
export const ConnectControllerModal: React.FC = () => {
  const hasBluetooth = useHasBluetoothLE();

  if (!hasBluetooth) return null;
  // eslint-disable-next-line react/jsx-pascal-case
  return <_ConnectControllerModal />;
};
